import React, { useState, useMemo } from 'react';
import { CompleteKundali, Language, ChartStyle } from '../../types/kundali';
import {
  calculateNativeLifeSeason,
  calculatePastMilestoneCalibrations,
  calculateAdaptiveMilestonePipeline,
  DEEP_QUESTIONS_REGISTRY,
  searchDeepQuestions,
  evaluateDeepQuestion,
  DeepQuestionDefinition,
  DeepAnswerResult,
  PurusharthaSphereId
} from '../../utils/v520/vedicDestinyMilestoneEngine';
import { calculateVedicDestinyUpayas } from '../../utils/v520/vedicDestinyUpayaEngine';
import { FiveRuleHouseCard } from '../v517/FiveRuleHouseCard';
import { TriangulationMatrixCard } from '../v519/TriangulationMatrixCard';
import { HoraRemedyScheduler } from '../v518/HoraRemedyScheduler';
import { AdvancedChartRail } from '../AdvancedChartRail';
import { LifePhaseForecastModule } from '../LifePhaseForecastModule';
import { LifeActivationTimelineView } from '../v600/LifeActivationTimelineView';
import { VartamanaSthitiView } from '../v600/VartamanaSthitiView';
import { PastMilestoneCalibrationView } from '../v600/PastMilestoneCalibrationView';
import { LifeDomainQuestionFrameworkView } from '../v600/LifeDomainQuestionFrameworkView';
import { LifePurposeDirectionView } from '../v600/LifePurposeDirectionView';
import { RelocationGeographyView } from '../v600/RelocationGeographyView';
import { SpiritualityIntelligenceView } from '../v600/SpiritualityIntelligenceView';
import { AuspiciousEssentialsBar } from '../AuspiciousEssentialsBar';
import { LifeThemeId } from '../../types/advancedLifeAnalysis';
import {
  Compass,
  Briefcase,
  Crown,
  Heart,
  Home,
  Globe,
  Activity,
  Shield,
  Coins,
  Scale,
  Calendar,
  Clock,
  ChevronRight,
  ChevronDown,
  Info,
  CheckCircle2,
  AlertTriangle,
  ArrowRight,
  BookOpen,
  Award,
  Layers,
  History,
  Sliders,
  Eye,
  Flame,
  Zap,
  Target,
  Sparkles,
  Send,
  HelpCircle,
  Gem,
  Sun,
  Search,
  Users
} from 'lucide-react';

interface VedicMilestoneDestinyStudioProps {
  kundali: CompleteKundali;
  lang?: Language;
  chartStyle?: ChartStyle;
  focusDate?: Date | string;
  onOpenConsultation?: (prompt?: string) => void;
}

type StudioTab = 'purpose' | 'vartamana' | 'calibration' | 'relocation' | 'activation' | 'horizon' | 'domains' | 'timing' | 'spirituality';

interface DomainOption {
  id: LifeThemeId;
  domainKey: string;
  sphereId: PurusharthaSphereId;
  nameEn: string;
  nameHi: string;
  bhavaNote: string;
  icon: React.ComponentType<{ className?: string }>;
  vargas: string[];
  defaultQuestionId: string;
}

const DOMAIN_OPTIONS: DomainOption[] = [
  {
    id: 'career_job',
    domainKey: 'career_dilemma',
    sphereId: 'artha',
    nameEn: 'Career & Enterprise',
    nameHi: 'आजीविका एवं व्यवसाय',
    bhavaNote: 'Bhava 10 & D-10',
    icon: Briefcase,
    vargas: ['D1', 'D10', 'D9'],
    defaultQuestionId: 'q_job_vs_business_verdict'
  },
  {
    id: 'business',
    domainKey: 'business_enterprise',
    sphereId: 'artha',
    nameEn: 'Commercial Venture & Startups',
    nameHi: 'व्यापार, स्टार्टअप व वाणिज्य',
    bhavaNote: 'Bhava 7, 10 & D-10',
    icon: Award,
    vargas: ['D1', 'D10', 'D9'],
    defaultQuestionId: 'q_commercial_business_launch'
  },
  {
    id: 'wealth_finance',
    domainKey: 'wealth_finance',
    sphereId: 'artha',
    nameEn: 'Wealth & Liquidity',
    nameHi: 'धन, कोष एवं लाभ',
    bhavaNote: 'Bhava 2 & 11',
    icon: Coins,
    vargas: ['D1', 'D2', 'D9'],
    defaultQuestionId: 'q_stock_speculation_wealth'
  },
  {
    id: 'marriage_relationship',
    domainKey: 'marriage_union',
    sphereId: 'kama',
    nameEn: 'Marriage & Partnership',
    nameHi: 'विवाह एवं दांपत्य',
    bhavaNote: 'Bhava 7 & D-9',
    icon: Heart,
    vargas: ['D1', 'D9', 'D7'],
    defaultQuestionId: 'q_marriage_love_vs_arranged'
  },
  {
    id: 'children',
    domainKey: 'progeny_children',
    sphereId: 'dharma',
    nameEn: 'Children & Legacy',
    nameHi: 'संतान, कुल एवं वंश',
    bhavaNote: 'Bhava 5 & D-7',
    icon: Crown,
    vargas: ['D1', 'D7', 'D9'],
    defaultQuestionId: 'q_progeny_conception_timing'
  },
  {
    id: 'education',
    domainKey: 'education_exams',
    sphereId: 'dharma',
    nameEn: 'Higher Education & Exams',
    nameHi: 'उच्च विद्या एवं प्रतियोगी परीक्षा',
    bhavaNote: 'Bhava 4, 5 & D-24',
    icon: BookOpen,
    vargas: ['D1', 'D24', 'D9'],
    defaultQuestionId: 'q_education_competitive_exam'
  },
  {
    id: 'house_property',
    domainKey: 'inheritance_estate',
    sphereId: 'dharma',
    nameEn: 'Property & Inheritance',
    nameHi: 'भूमि, भवन व पैतृक संपत्ति',
    bhavaNote: 'Bhava 4, 8 & D-4, D-8',
    icon: Home,
    vargas: ['D1', 'D4', 'D8'],
    defaultQuestionId: 'q_inheritance_batwara_timing'
  },
  {
    id: 'vehicle',
    domainKey: 'vehicle_comfort',
    sphereId: 'artha',
    nameEn: 'Vehicles & Conveyances',
    nameHi: 'वाहन सुख व सुरक्षा',
    bhavaNote: 'Bhava 4 & D-16',
    icon: Target,
    vargas: ['D1', 'D16', 'D4'],
    defaultQuestionId: 'q_vehicle_purchase_comfort'
  },
  {
    id: 'health_vitality',
    domainKey: 'health_vitality',
    sphereId: 'moksha',
    nameEn: 'Health & Vitality',
    nameHi: 'स्वास्थ्य एवं जीवन शक्ति',
    bhavaNote: 'Bhava 1 & 6',
    icon: Activity,
    vargas: ['D1', 'D9', 'D30'],
    defaultQuestionId: 'q_health_surgery_longevity'
  },
  {
    id: 'foreign_travel',
    domainKey: 'foreign_horizons',
    sphereId: 'moksha',
    nameEn: 'Foreign PR & Travel',
    nameHi: 'विदेश निवास व नागरिकता',
    bhavaNote: 'Bhava 9 & 12',
    icon: Globe,
    vargas: ['D1', 'D9', 'D12'],
    defaultQuestionId: 'q_foreign_pr_vs_homeland'
  },
  {
    id: 'litigation_competition',
    domainKey: 'reputation_crisis',
    sphereId: 'dharma',
    nameEn: 'Protection & Court Cases',
    nameHi: 'विधिक संरक्षण व प्रतिष्ठा',
    bhavaNote: 'Bhava 6 & 8',
    icon: Shield,
    vargas: ['D1', 'D9', 'D6'],
    defaultQuestionId: 'q_scandals_loss_of_face'
  },
  {
    id: 'spirituality',
    domainKey: 'spirituality_moksha',
    sphereId: 'moksha',
    nameEn: 'Spirituality & Inner Path',
    nameHi: 'अध्यात्म व आंतरिक साधना',
    bhavaNote: 'Bhava 9 & 12',
    icon: Sparkles,
    vargas: ['D1', 'D9', 'D20'],
    defaultQuestionId: 'q_spiritual_guru_diksha'
  },
  {
    id: 'family_parents',
    domainKey: 'siblings_family',
    sphereId: 'dharma',
    nameEn: 'Siblings & Family Bond',
    nameHi: 'सहोदर स्नेह व पारिवारिक शांति',
    bhavaNote: 'Bhava 3 & 11',
    icon: Users,
    vargas: ['D1', 'D3'],
    defaultQuestionId: 'q_sibling_support_harmony'
  }
];

export const VedicMilestoneDestinyStudio: React.FC<VedicMilestoneDestinyStudioProps> = ({
  kundali,
  lang = 'en',
  chartStyle = 'north',
  focusDate,
  onOpenConsultation
}) => {
  const isHi = lang === 'hi';

  // --------------------------------------------------------------------------
  // Core Studio Navigation State (Vartamana Sthiti + 4 Unified Views)
  // --------------------------------------------------------------------------
  const [activeTab, setActiveTab] = useState<StudioTab>('vartamana');
  const [periodEpoch, setPeriodEpoch] = useState<'current_pratyantar' | 'current_antar' | 'mahadasha_arc'>('current_antar');
  const [selectedDomainId, setSelectedDomainId] = useState<LifeThemeId>('career_job');
  const [selectedQuestionId, setSelectedQuestionId] = useState<string>('q_job_vs_business_verdict');
  const [questionSearchQuery, setQuestionSearchQuery] = useState<string>('');
  const [showPastCalibration, setShowPastCalibration] = useState<boolean>(false);
  const [activeForensicTab, setActiveForensicTab] = useState<'five_rule' | 'triangulation' | 'varga' | 'shastras'>('five_rule');
  const [aiQuestionPrompt, setAiQuestionPrompt] = useState<string>('');

  // --------------------------------------------------------------------------
  // Calculated Engines
  // --------------------------------------------------------------------------
  const { currentSeason, currentAge } = useMemo(() => {
    return calculateNativeLifeSeason(kundali);
  }, [kundali]);

  const pastCalibrations = useMemo(() => {
    return calculatePastMilestoneCalibrations(kundali);
  }, [kundali]);

  const milestonePipeline = useMemo(() => {
    return calculateAdaptiveMilestonePipeline(kundali, periodEpoch);
  }, [kundali, periodEpoch]);

  const selectedDomain = useMemo(() => {
    return DOMAIN_OPTIONS.find(d => d.id === selectedDomainId) || DOMAIN_OPTIONS[0];
  }, [selectedDomainId]);

  // Questions for current domain & search
  const domainQuestions = useMemo(() => {
    if (questionSearchQuery.trim()) {
      return searchDeepQuestions(questionSearchQuery);
    }
    const filtered = DEEP_QUESTIONS_REGISTRY.filter(
      q => q.domainId === selectedDomain.domainKey || q.sphereId === selectedDomain.sphereId
    );
    return filtered.length > 0 ? filtered : [DEEP_QUESTIONS_REGISTRY[0]];
  }, [selectedDomain, questionSearchQuery]);

  // Evaluate active question
  const activeEvaluation: DeepAnswerResult | null = useMemo(() => {
    return evaluateDeepQuestion(selectedQuestionId, kundali, lang);
  }, [selectedQuestionId, kundali, lang]);

  const activeQuestionDef = useMemo(() => {
    return DEEP_QUESTIONS_REGISTRY.find(q => q.id === selectedQuestionId) || DEEP_QUESTIONS_REGISTRY[0];
  }, [selectedQuestionId]);

  // Resolved Focus Date
  const resolvedFocusDate = useMemo(() => {
    return focusDate ? new Date(focusDate) : new Date();
  }, [focusDate]);

  // Dynamic Vedic Destiny Prescriptive Upayas & Panchanga Cosmic Timing Context
  const destinyUpayas = useMemo(() => {
    return calculateVedicDestinyUpayas(kundali, resolvedFocusDate, lang);
  }, [kundali, resolvedFocusDate, lang]);

  // Active Dasha String (strictly grounded in resolved focusDate)
  const activeDashaSummary = useMemo(() => {
    return destinyUpayas.dashaPathSummary;
  }, [destinyUpayas]);

  // Handle Domain Selection
  const handleSelectDomain = (domain: DomainOption) => {
    setSelectedDomainId(domain.id);
    setSelectedQuestionId(domain.defaultQuestionId);
    setQuestionSearchQuery('');
  };

  const handleAskAcharya = (customPrompt?: string) => {
    const promptToSend = customPrompt || aiQuestionPrompt || `Analyze my ${selectedDomain.nameEn} outlook under ${activeDashaSummary} dasha.`;
    if (onOpenConsultation) {
      onOpenConsultation(promptToSend);
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12 text-astro-text">
      {/* =================================================================== */}
      {/* STUDIO HEADER: COMPACT KARMIC ANCHOR & SECONDARY NAVIGATION BAR    */}
      {/* =================================================================== */}
      <div className="bg-astro-card border border-astro-border rounded-2xl p-4 sm:p-5 shadow-xs">
        {/* Row 1: Compact Title, Subtitle, & Metadata Row */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div className="space-y-1">
            <div className="flex items-center gap-2 flex-wrap text-xs">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md bg-astro-sage-tint text-astro-primary border border-astro-primary/30 font-semibold">
                <Compass className="w-3.5 h-3.5 text-astro-accent" />
                {isHi ? 'वैदिक डेस्टिनी स्टूडियो' : 'Vedic Destiny Studio'}
              </span>
              <span className="text-astro-muted">•</span>
              <span className="text-xs px-2 py-0.5 rounded-md bg-astro-secondary-surface text-astro-secondary-text border border-astro-border shadow-2xs font-medium">
                {isHi ? `आयु: ${currentAge} वर्ष` : `Age: ${currentAge} Yrs`} · {currentSeason.sanskritTerm}
              </span>
              <span className="text-astro-muted">•</span>
              <span className="text-xs px-2.5 py-0.5 rounded-md bg-astro-sage-tint text-astro-primary border border-astro-primary/30 font-semibold">
                {isHi ? 'सक्रिय दशा:' : 'Active Dasha:'} {activeDashaSummary}
              </span>
            </div>

            <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-astro-text font-serif tracking-tight mt-1">
              {isHi ? 'काल-चक्र जीवन सिद्धि एवं कर्म फल स्टूडियो' : 'Vedic Destiny & Life Milestone Studio'}
            </h1>
            <p className="text-xs text-astro-secondary-text max-w-3xl leading-relaxed">
              {isHi
                ? 'सटीक दशा कालानुक्रम, पुरुषार्थ जीवन मील के पत्थर एवं शास्त्रीय ग्रह उपाय'
                : 'Deterministic Dasha–Bhukti horizon timing, life milestone forecasts, and surgical Parashari Upayas.'}
            </p>
          </div>
        </div>

        {/* Auspicious Essentials: Lucky Numbers, Colors, Gem & Direction */}
        <div className="mt-3.5">
          <AuspiciousEssentialsBar
            kundali={kundali}
            lang={lang}
            onOpenNumerology={() => {
              window.dispatchEvent(new CustomEvent('astro_navigate_tab', { detail: 'numerology' }));
            }}
            onOpenGemstones={() => {
              window.dispatchEvent(new CustomEvent('astro_navigate_tab', { detail: 'gemstones' }));
            }}
          />
        </div>

        {/* Row 2: Dedicated Full-Width Secondary Navigation Bar */}
        <div className="mt-3.5 pt-3.5 border-t border-astro-border/70">
          <div className="flex items-center p-1.5 rounded-xl bg-astro-secondary-surface/70 border border-astro-border shadow-2xs overflow-x-auto custom-horizontal-scrollbar feature-nav-scroll gap-1.5">
            <button
              onClick={() => setActiveTab('purpose')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all shrink-0 cursor-pointer ${
                activeTab === 'purpose'
                  ? 'bg-astro-primary text-white font-bold shadow-xs'
                  : 'text-astro-secondary-text hover:text-astro-primary hover:bg-astro-card border border-transparent'
              }`}
            >
              <Compass className={`w-3.5 h-3.5 ${activeTab === 'purpose' ? 'text-astro-accent' : 'text-astro-secondary-text'}`} />
              <span>{isHi ? 'जीवन उद्देश्य व स्वधर्म' : 'Life Purpose & Direction'}</span>
            </button>

            <button
              onClick={() => setActiveTab('vartamana')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all shrink-0 cursor-pointer ${
                activeTab === 'vartamana'
                  ? 'bg-astro-primary text-white font-bold shadow-xs'
                  : 'text-astro-secondary-text hover:text-astro-primary hover:bg-astro-card border border-transparent'
              }`}
            >
              <Activity className={`w-3.5 h-3.5 ${activeTab === 'vartamana' ? 'text-astro-accent' : 'text-astro-secondary-text'}`} />
              <span>{isHi ? 'वर्तमान स्थिति' : 'Vartamāna Sthiti'}</span>
            </button>

            <button
              onClick={() => setActiveTab('calibration')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all shrink-0 cursor-pointer ${
                activeTab === 'calibration'
                  ? 'bg-astro-primary text-white font-bold shadow-xs'
                  : 'text-astro-secondary-text hover:text-astro-primary hover:bg-astro-card border border-transparent'
              }`}
            >
              <History className={`w-3.5 h-3.5 ${activeTab === 'calibration' ? 'text-astro-accent' : 'text-astro-secondary-text'}`} />
              <span>{isHi ? 'अतीत सत्यापन' : 'Past Calibration'}</span>
            </button>

            <button
              onClick={() => setActiveTab('relocation')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all shrink-0 cursor-pointer ${
                activeTab === 'relocation'
                  ? 'bg-astro-primary text-white font-bold shadow-xs'
                  : 'text-astro-secondary-text hover:text-astro-primary hover:bg-astro-card border border-transparent'
              }`}
            >
              <Globe className={`w-3.5 h-3.5 ${activeTab === 'relocation' ? 'text-astro-accent' : 'text-astro-secondary-text'}`} />
              <span>{isHi ? 'स्थान व विदेश' : 'Relocation & Geography'}</span>
            </button>

            <button
              onClick={() => setActiveTab('activation')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all shrink-0 cursor-pointer ${
                activeTab === 'activation'
                  ? 'bg-astro-primary text-white font-bold shadow-xs'
                  : 'text-astro-secondary-text hover:text-astro-primary hover:bg-astro-card border border-transparent'
              }`}
            >
              <Layers className={`w-3.5 h-3.5 ${activeTab === 'activation' ? 'text-astro-accent' : 'text-astro-secondary-text'}`} />
              <span>{isHi ? 'सक्रियता कालक्रम' : 'Life Activation'}</span>
            </button>

            <button
              onClick={() => setActiveTab('horizon')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all shrink-0 cursor-pointer ${
                activeTab === 'horizon'
                  ? 'bg-astro-primary text-white font-bold shadow-xs'
                  : 'text-astro-secondary-text hover:text-astro-primary hover:bg-astro-card border border-transparent'
              }`}
            >
              <Calendar className={`w-3.5 h-3.5 ${activeTab === 'horizon' ? 'text-astro-accent' : 'text-astro-secondary-text'}`} />
              <span>{isHi ? 'जीवन चरण' : 'Seasons & Milestones'}</span>
            </button>

            <button
              onClick={() => setActiveTab('domains')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all shrink-0 cursor-pointer ${
                activeTab === 'domains'
                  ? 'bg-astro-primary text-white font-bold shadow-xs'
                  : 'text-astro-secondary-text hover:text-astro-primary hover:bg-astro-card border border-transparent'
              }`}
            >
              <Target className={`w-3.5 h-3.5 ${activeTab === 'domains' ? 'text-astro-accent' : 'text-astro-secondary-text'}`} />
              <span>{isHi ? '12 जीवन क्षेत्र' : '12 Life Domains'}</span>
            </button>

            <button
              onClick={() => setActiveTab('timing')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all shrink-0 cursor-pointer ${
                activeTab === 'timing'
                  ? 'bg-astro-primary text-white font-bold shadow-xs'
                  : 'text-astro-secondary-text hover:text-astro-primary hover:bg-astro-card border border-transparent'
              }`}
            >
              <Clock className={`w-3.5 h-3.5 ${activeTab === 'timing' ? 'text-astro-accent' : 'text-astro-secondary-text'}`} />
              <span>{isHi ? 'उपाय व होरा' : 'Upayas & Timing'}</span>
            </button>

            <button
              onClick={() => setActiveTab('spirituality')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all shrink-0 cursor-pointer ${
                activeTab === 'spirituality'
                  ? 'bg-astro-primary text-white font-bold shadow-xs'
                  : 'text-astro-secondary-text hover:text-astro-primary hover:bg-astro-card border border-transparent'
              }`}
            >
              <Sparkles className={`w-3.5 h-3.5 ${activeTab === 'spirituality' ? 'text-astro-accent' : 'text-astro-secondary-text'}`} />
              <span>{isHi ? 'आध्यात्मिक पथ' : 'Spirituality & Sadhana'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* =================================================================== */}
      {/* VIEW: LIFE PURPOSE & DIRECTION (SVADHARMA & 6-LAYER HIERARCHY)      */}
      {/* =================================================================== */}
      {activeTab === 'purpose' && (
        <LifePurposeDirectionView
          kundali={kundali}
          lang={lang}
          chartStyle={chartStyle}
          focusDate={focusDate}
          onOpenConsultation={onOpenConsultation}
        />
      )}

      {/* =================================================================== */}
      {/* VIEW: RELOCATION & GEOGRAPHY (10-DIMENSION FORENSIC ANALYSIS)       */}
      {/* =================================================================== */}
      {activeTab === 'relocation' && (
        <RelocationGeographyView
          kundali={kundali}
          lang={lang}
          chartStyle={chartStyle}
          focusDate={focusDate}
          onOpenConsultation={onOpenConsultation}
        />
      )}

      {/* =================================================================== */}
      {/* VIEW: PAST MILESTONE CALIBRATION WORKBENCH                          */}
      {/* =================================================================== */}
      {activeTab === 'calibration' && (
        <PastMilestoneCalibrationView
          kundali={kundali}
          lang={lang}
          chartStyle={chartStyle}
          focusDate={focusDate}
          onOpenConsultation={onOpenConsultation}
        />
      )}

      {/* =================================================================== */}
      {/* VIEW: VARTAMĀNA STHITI & DYNAMIC JYOTISH TIMELINE (V6.0 CORE)       */}
      {/* =================================================================== */}
      {activeTab === 'vartamana' && (
        <VartamanaSthitiView
          kundali={kundali}
          lang={lang}
          onOpenConsultation={onOpenConsultation}
        />
      )}

      {/* =================================================================== */}
      {/* VIEW 0: LIFE DOMAIN ACTIVATION & HORIZON TIMELINE (LDAHE-V6.0)       */}
      {/* =================================================================== */}
      {activeTab === 'activation' && (
        <LifeActivationTimelineView
          kundali={kundali}
          lang={lang}
          chartStyle={chartStyle}
          focusDate={focusDate}
          onOpenConsultation={onOpenConsultation}
        />
      )}

      {/* =================================================================== */}
      {/* VIEW 1: LIFE HORIZON & ACTIVE SEASON (TIMELINE & CALIBRATION)        */}
      {/* =================================================================== */}
      {activeTab === 'horizon' && (
        <div className="space-y-6">
          {/* Active Karmic Season & Age Banner */}
          <div className="bg-astro-card border border-astro-border rounded-2xl p-6 shadow-xs relative overflow-hidden">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="space-y-1.5 max-w-3xl">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold tracking-wider uppercase text-astro-primary bg-astro-secondary-surface px-2.5 py-0.5 rounded border border-astro-border">
                    {isHi ? 'सक्रिय जीवन चरण' : 'Active Life Season'}
                  </span>
                  <span className="text-xs font-mono font-semibold text-astro-accent">
                    Age {currentAge} • {currentSeason.ageRange}
                  </span>
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-astro-text font-serif">
                  {isHi ? currentSeason.nameHi : currentSeason.nameEn} ({currentSeason.sanskritTerm})
                </h2>
                <p className="text-xs md:text-sm text-astro-secondary-text leading-relaxed">
                  {isHi ? currentSeason.descriptionHi : currentSeason.descriptionEn}
                </p>
                <div className="pt-2 flex flex-wrap items-center gap-2 text-xs font-medium">
                  <span className="text-astro-primary font-bold">{isHi ? 'शास्त्रीय मुख्य ग्रह:' : 'Ruling Planets:'}</span>
                  <span className="bg-astro-secondary-surface px-2.5 py-1 rounded-md border border-astro-border text-astro-text shadow-2xs font-mono font-bold text-astro-primary">
                    {currentSeason.rulingPlanets.join(', ')}
                  </span>
                </div>
              </div>

              {/* Dasha-Bhukti Convergence Indicator */}
              <div className="bg-astro-secondary-surface border border-astro-border rounded-xl p-4 shrink-0 shadow-2xs space-y-2 min-w-[240px]">
                <div className="flex items-center justify-between text-xs border-b border-astro-border pb-1.5 font-bold">
                  <span className="text-astro-secondary-text">{isHi ? 'दशा संमिलन' : 'Dasha Resonance'}</span>
                  <span className="text-astro-primary font-mono font-bold">Resonant 94%</span>
                </div>
                <div className="text-xs space-y-1">
                  <div className="flex justify-between">
                    <span className="text-astro-muted">{isHi ? 'महादशा' : 'Mahadasha'}:</span>
                    <span className="font-bold text-astro-text">{destinyUpayas.activeMahadashaLord}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-astro-muted">{isHi ? 'अंतर्दशा' : 'Antardasha'}:</span>
                    <span className="font-bold text-astro-text">{destinyUpayas.activeAntardashaLord}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-astro-muted">{isHi ? 'प्रत्यंतर' : 'Pratyantar'}:</span>
                    <span className="font-bold text-astro-text">{destinyUpayas.activePratyantardashaLord ? `${destinyUpayas.activePratyantardashaLord} (Active)` : 'Active'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Life Phase Forecast & Season Transition Engine (Astrological Period Outlook) */}
          <LifePhaseForecastModule
            kundali={kundali}
            selectedThemeId={selectedDomainId}
            lang={lang}
            defaultEpoch="current_antar"
          />

          {/* Master Interactive Timeline View Selector */}
          <div className="bg-astro-card border border-astro-border rounded-2xl p-6 shadow-xs space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-astro-border pb-4">
              <div>
                <h3 className="text-base font-bold text-astro-text flex items-center gap-2 font-serif">
                  <Calendar className="w-4 h-4 text-astro-accent" />
                  <span>{isHi ? 'मास्टर वैदिक मील का पत्थर कालक्रम' : 'Master Vedic Destiny Milestones Pipeline'}</span>
                </h3>
                <p className="text-xs text-astro-secondary-text">
                  {isHi
                    ? 'दशा-गोचर-अष्टकवर्ग के आधार पर कालक्रम अनुसार संभावित जीवन घटनाएं'
                    : 'Deterministic Parashari temporal convergence mapped across astrological dasha epochs.'}
                </p>
              </div>

              {/* Astrological Period Granularity Toggle */}
              <div className="flex items-center p-1 rounded-lg bg-astro-secondary-surface border border-astro-border shadow-2xs gap-1">
                <button
                  onClick={() => setPeriodEpoch('current_pratyantar')}
                  className={`px-3 py-1 rounded text-xs font-semibold transition-all cursor-pointer ${
                    periodEpoch === 'current_pratyantar'
                      ? 'bg-astro-primary text-white shadow-2xs'
                      : 'text-astro-secondary-text hover:text-astro-primary'
                  }`}
                >
                  {isHi ? 'सक्रिय प्रत्यंतर' : 'Active PD Window'}
                </button>
                <button
                  onClick={() => setPeriodEpoch('current_antar')}
                  className={`px-3 py-1 rounded text-xs font-semibold transition-all cursor-pointer ${
                    periodEpoch === 'current_antar'
                      ? 'bg-astro-primary text-white shadow-2xs'
                      : 'text-astro-secondary-text hover:text-astro-primary'
                  }`}
                >
                  {isHi ? 'सक्रिय अंतर्दशा' : 'Active Antardasha'}
                </button>
                <button
                  onClick={() => setPeriodEpoch('mahadasha_arc')}
                  className={`px-3 py-1 rounded text-xs font-semibold transition-all cursor-pointer ${
                    periodEpoch === 'mahadasha_arc'
                      ? 'bg-astro-primary text-white shadow-2xs'
                      : 'text-astro-secondary-text hover:text-astro-primary'
                  }`}
                >
                  {isHi ? 'संपूर्ण महादशा' : 'Full Mahadasha Arc'}
                </button>
              </div>
            </div>

            {/* Pipeline Stage Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {milestonePipeline.map((phase) => {
                const isCurrent = phase.phaseNumber === 1;
                return (
                  <div
                    key={phase.id}
                    className={`rounded-xl border p-4 transition-all space-y-3 ${
                      isCurrent
                        ? 'bg-astro-card border-2 border-astro-primary shadow-sm ring-1 ring-astro-primary/20'
                        : 'bg-astro-secondary-surface border-astro-border shadow-2xs hover:border-astro-accent/50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider ${
                        isCurrent
                          ? 'bg-astro-sage-tint text-astro-primary border border-astro-primary/30 font-bold'
                          : 'bg-astro-card text-astro-secondary-text border border-astro-border'
                      }`}>
                        {isCurrent ? (isHi ? '● वर्तमान सक्रिय' : '● ACTIVE NOW') : `Phase ${phase.phaseNumber}`}
                      </span>
                      <span className="text-xs font-mono font-bold text-astro-muted">{phase.targetYearRange}</span>
                    </div>

                    <div>
                      <h4 className="text-sm font-bold text-astro-text font-serif">
                        {isHi ? phase.titleHi : phase.titleEn}
                      </h4>
                      <p className="text-xs text-astro-secondary-text mt-1 line-clamp-2">
                        {isHi ? phase.coreStrategyHi : phase.coreStrategyEn}
                      </p>
                    </div>

                    <div className="space-y-1.5 pt-2 border-t border-astro-border text-xs">
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-astro-muted">{isHi ? 'दशा संरेखन:' : 'Dasha Trigger:'}</span>
                        <span className="font-mono font-semibold text-astro-primary">{phase.dashaTrigger}</span>
                      </div>
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-astro-muted">{isHi ? 'विश्वास स्कोर:' : 'Confidence Score:'}</span>
                        <span className="font-semibold text-astro-primary font-mono">{phase.confidenceScore}%</span>
                      </div>
                      <div className="bg-astro-card p-2 rounded text-[11px] text-astro-text border border-astro-border mt-2">
                        <strong className="text-astro-primary font-bold">{isHi ? 'प्रेरक कारक: ' : 'Catalyst: '}</strong>
                        {isHi ? phase.catalystHi : phase.catalystEn}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Past Calibration Drawer Toggle */}
            <div className="pt-2 border-t border-astro-border flex items-center justify-between">
              <button
                onClick={() => setShowPastCalibration(!showPastCalibration)}
                className="text-xs font-bold text-astro-primary hover:text-astro-accent flex items-center gap-1.5 cursor-pointer"
              >
                <History className="w-4 h-4" />
                <span>
                  {showPastCalibration
                    ? (isHi ? 'अतीत मील का पत्थर सत्यापन छुपाएं' : 'Hide Past Milestone Calibrations')
                    : (isHi ? 'अतीत मील का पत्थर सत्यापन देखें (Accuracy Check)' : 'View Past Milestone Calibrations (Accuracy Check)')}
                </span>
                {showPastCalibration ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              </button>
              <span className="text-[11px] text-astro-secondary-text">
                {isHi ? 'भूतकाल की घटनाओं से कुंडली की सटीकता की पुष्टि' : 'Calibrates chart accuracy against past major life milestones'}
              </span>
            </div>

            {/* Collapsible Past Calibration Workbench */}
            {showPastCalibration && (
              <div className="pt-2 animate-in fade-in duration-200">
                <PastMilestoneCalibrationView
                  kundali={kundali}
                  lang={lang}
                  chartStyle={chartStyle}
                  onOpenConsultation={onOpenConsultation}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* =================================================================== */}
      {/* VIEW 2: LIFE DOMAIN DEEP DIVE (12 LIFE DOMAINS DYNAMIC FRAMEWORK)   */}
      {/* =================================================================== */}
      {activeTab === 'domains' && (
        <LifeDomainQuestionFrameworkView
          kundali={kundali}
          lang={lang}
          chartStyle={chartStyle}
          focusDate={focusDate}
          onOpenConsultation={onOpenConsultation}
        />
      )}

      {/* =================================================================== */}
      {/* VIEW 3: SURGICAL UPAYAS & MUHURTA TIMING (ACTIONABLE PRACTICE)      */}
      {/* =================================================================== */}
      {activeTab === 'timing' && (
        <div className="space-y-6">
          {/* Active Phase Prescriptions Card */}
          <div className="bg-astro-card border border-astro-border rounded-2xl p-6 shadow-xs space-y-4">
            <div className="flex items-center justify-between border-b border-astro-border pb-3">
              <div>
                <h3 className="text-base font-bold text-astro-text flex items-center gap-2 font-serif">
                  <Sparkles className="w-4 h-4 text-astro-accent" />
                  <span>{isHi ? 'वर्तमान दशा-विशिष्ट वैदिक उपाय एवं साधना' : 'Active Dasha-Bhukti Prescribed Upayas'}</span>
                </h3>
                <p className="text-xs text-astro-secondary-text">
                  {isHi
                    ? `वर्तमान ${activeDashaSummary} दशा एवं ग्रह स्थितियों के अनुसार शास्त्रीय निवारक उपाय`
                    : `Surgically harmonized remedies targeting ${activeDashaSummary} dasha lords and maraka/dhaiyya mitigations.`}
                </p>
              </div>
              <span className="text-xs px-2.5 py-1 rounded-md bg-astro-secondary-surface text-astro-secondary-text border border-astro-border font-medium">
                {isHi ? 'व्यक्तिगत समाधान' : 'Customized Upayas'}
              </span>
            </div>

            {/* Panchanga Cosmic Timing & Observance Grounding Strip */}
            <div className={`p-3.5 rounded-xl border text-xs flex flex-col sm:flex-row sm:items-center justify-between gap-2 shadow-2xs ${
              destinyUpayas.panchangaTimingContext.isVaraAlignedWithUpaya || destinyUpayas.panchangaTimingContext.hasActiveVrat || destinyUpayas.panchangaTimingContext.isSankrantiActive || destinyUpayas.panchangaTimingContext.isPitruPakshaActive
                ? 'bg-amber-50/80 border-amber-300 text-amber-950 dark:bg-amber-950/30 dark:border-amber-700 dark:text-amber-200'
                : 'bg-astro-secondary-surface border-astro-border text-astro-secondary-text'
            }`}>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-astro-accent shrink-0" />
                <span className="font-semibold">
                  {isHi ? destinyUpayas.panchangaTimingContext.dailyGuidanceHi : destinyUpayas.panchangaTimingContext.dailyGuidanceEn}
                </span>
              </div>
              <div className="flex items-center gap-1.5 shrink-0 font-mono text-[11px]">
                <span className="px-2 py-0.5 rounded bg-white/80 dark:bg-astro-card border border-astro-border font-bold">
                  {isHi ? destinyUpayas.panchangaTimingContext.varaNameHi : destinyUpayas.panchangaTimingContext.varaNameEn}
                </span>
                <span className="px-2 py-0.5 rounded bg-white/80 dark:bg-astro-card border border-astro-border">
                  {isHi ? destinyUpayas.panchangaTimingContext.tithiNameHi : destinyUpayas.panchangaTimingContext.tithiNameEn}
                </span>
              </div>
            </div>

            {/* 4 Dynamic Prescriptive Cards Grounded in Active Dasha & Panchanga */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Mantra Sadhana */}
              <div className="p-4 rounded-xl bg-astro-secondary-surface border border-astro-border space-y-2 shadow-2xs">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-astro-text flex items-center gap-1.5">
                    <Sun className="w-4 h-4 text-astro-accent" />
                    {isHi ? destinyUpayas.cards.mantra.titleHi : destinyUpayas.cards.mantra.titleEn}
                  </span>
                  <span className="text-[10px] px-2 py-0.5 rounded bg-astro-card text-astro-secondary-text border border-astro-border font-medium">
                    {isHi ? destinyUpayas.cards.mantra.primaryActionHi : destinyUpayas.cards.mantra.primaryActionEn}
                  </span>
                </div>
                <div className="font-mono text-xs text-astro-text bg-astro-card p-2.5 rounded border border-astro-border font-semibold">
                  {destinyUpayas.cards.mantra.prescribedText}
                </div>
                <p className="text-[11px] text-astro-secondary-text">
                  {isHi ? destinyUpayas.cards.mantra.shastricRuleHi : destinyUpayas.cards.mantra.shastricRuleEn}
                </p>
                {destinyUpayas.cards.mantra.panchangaAlignmentNoticeEn && (
                  <div className="text-[10px] text-emerald-800 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 p-1.5 rounded border border-emerald-200 dark:border-emerald-800 font-medium">
                    {isHi ? destinyUpayas.cards.mantra.panchangaAlignmentNoticeHi : destinyUpayas.cards.mantra.panchangaAlignmentNoticeEn}
                  </div>
                )}
              </div>

              {/* Sacred Stotra */}
              <div className="p-4 rounded-xl bg-astro-secondary-surface border border-astro-border space-y-2 shadow-2xs">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-astro-text flex items-center gap-1.5">
                    <BookOpen className="w-4 h-4 text-astro-accent" />
                    {isHi ? destinyUpayas.cards.stotra.titleHi : destinyUpayas.cards.stotra.titleEn}
                  </span>
                  <span className="text-[10px] px-2 py-0.5 rounded bg-astro-card text-astro-secondary-text border border-astro-border font-medium">
                    {isHi ? destinyUpayas.cards.stotra.primaryActionHi : destinyUpayas.cards.stotra.primaryActionEn}
                  </span>
                </div>
                <div className="font-serif text-xs text-astro-text bg-astro-card p-2.5 rounded border border-astro-border font-bold">
                  {destinyUpayas.cards.stotra.prescribedText}
                </div>
                <p className="text-[11px] text-astro-secondary-text">
                  {isHi ? destinyUpayas.cards.stotra.shastricRuleHi : destinyUpayas.cards.stotra.shastricRuleEn}
                </p>
                <div className="text-[10px] text-astro-muted font-mono">
                  {isHi ? destinyUpayas.cards.stotra.timingGuidanceHi : destinyUpayas.cards.stotra.timingGuidanceEn}
                </div>
              </div>

              {/* Consecrated Gemstone */}
              <div className="p-4 rounded-xl bg-astro-secondary-surface border border-astro-border space-y-2 shadow-2xs">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-astro-text flex items-center gap-1.5">
                    <Gem className="w-4 h-4 text-astro-accent" />
                    {isHi ? destinyUpayas.cards.gemstone.titleHi : destinyUpayas.cards.gemstone.titleEn}
                  </span>
                  <span className="text-[10px] px-2 py-0.5 rounded bg-astro-sage-tint text-astro-primary border border-astro-primary/30 font-medium">
                    {isHi ? destinyUpayas.cards.gemstone.roleHi : destinyUpayas.cards.gemstone.roleEn}
                  </span>
                </div>
                <div className="text-xs font-bold text-astro-text bg-astro-card p-2.5 rounded border border-astro-border flex items-center justify-between">
                  <span>{destinyUpayas.cards.gemstone.prescribedText}</span>
                  <span className="text-[11px] text-astro-secondary-text font-mono">
                    {isHi ? destinyUpayas.cards.gemstone.primaryActionHi : destinyUpayas.cards.gemstone.primaryActionEn}
                  </span>
                </div>
                <p className="text-[11px] text-astro-secondary-text">
                  {isHi ? destinyUpayas.cards.gemstone.timingGuidanceHi : destinyUpayas.cards.gemstone.timingGuidanceEn}
                </p>
                <div className="text-[10px] text-astro-muted">
                  {isHi ? destinyUpayas.cards.gemstone.shastricRuleHi : destinyUpayas.cards.gemstone.shastricRuleEn}
                </div>
              </div>

              {/* Seva / Charity */}
              <div className="p-4 rounded-xl bg-astro-secondary-surface border border-astro-border space-y-2 shadow-2xs">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-astro-text flex items-center gap-1.5">
                    <Heart className="w-4 h-4 text-astro-accent" />
                    {isHi ? destinyUpayas.cards.seva.titleHi : destinyUpayas.cards.seva.titleEn}
                  </span>
                  <span className="text-[10px] px-2 py-0.5 rounded bg-astro-card text-astro-secondary-text border border-astro-border font-medium">
                    {isHi ? destinyUpayas.cards.seva.primaryActionHi : destinyUpayas.cards.seva.primaryActionEn}
                  </span>
                </div>
                <div className="text-xs text-astro-text bg-astro-card p-2.5 rounded border border-astro-border font-medium">
                  {destinyUpayas.cards.seva.prescribedText}
                </div>
                <p className="text-[11px] text-astro-secondary-text">
                  {isHi ? destinyUpayas.cards.seva.shastricRuleHi : destinyUpayas.cards.seva.shastricRuleEn}
                </p>
                <div className="text-[10px] text-astro-muted font-mono">
                  {isHi ? destinyUpayas.cards.seva.timingGuidanceHi : destinyUpayas.cards.seva.timingGuidanceEn}
                </div>
              </div>
            </div>
          </div>

          {/* Live 24-Hour Chaldean Hora Scheduler */}
          <HoraRemedyScheduler
            kundali={kundali}
            lang={lang}
          />

          {/* Interactive Acharya AI Counsel Input */}
          <div className="bg-astro-card border border-astro-border rounded-2xl p-6 shadow-xs space-y-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-astro-accent" />
              <h3 className="text-sm font-bold text-astro-text font-serif">
                {isHi ? 'ज्योतिष आचार्य से प्रत्यक्ष परामर्श' : 'Dispatch Question to Acharya Consultation AI'}
              </h3>
            </div>
            <p className="text-xs text-astro-secondary-text">
              {isHi
                ? 'अपनी वर्तमान स्थिति या निर्णय के बारे में विशिष्ट प्रश्न पूछें, जो आपकी कुंडली के शास्त्रीय प्रमाणों से जांचा जाएगा:'
                : 'Pose specific situational questions directly grounded in your verified Parashari planetary evidence:'}
            </p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={aiQuestionPrompt}
                onChange={(e) => setAiQuestionPrompt(e.target.value)}
                placeholder={isHi ? 'उदा: क्या मुझे अभी नई नौकरी में जाना चाहिए अथवा व्यवसाय शुरू करना चाहिए?' : 'e.g. Should I accept the overseas offer or wait for the Q1 executive promotion?'}
                className="flex-1 px-4 py-2.5 rounded-xl border border-astro-border bg-astro-secondary-surface text-xs text-astro-text placeholder:text-astro-muted focus:outline-none focus:ring-2 focus:ring-astro-primary"
                onKeyDown={(e) => e.key === 'Enter' && handleAskAcharya()}
              />
              <button
                onClick={() => handleAskAcharya()}
                className="px-4 py-2.5 rounded-xl bg-astro-primary text-white text-xs font-bold hover:bg-astro-secondary transition-all flex items-center gap-1.5 cursor-pointer shadow-2xs"
              >
                <Send className="w-3.5 h-3.5" />
                <span>{isHi ? 'परामर्श लें' : 'Consult'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =================================================================== */}
      {/* VIEW: SPIRITUALITY INTELLIGENCE (SADHANA, VAIRAGYA, DHARMA-MOKSHA)  */}
      {/* =================================================================== */}
      {activeTab === 'spirituality' && (
        <SpiritualityIntelligenceView
          kundali={kundali}
          lang={lang}
          focusDate={focusDate}
          onOpenConsultation={onOpenConsultation}
        />
      )}
    </div>
  );
};
